import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Zap, ArrowRight, Upload } from 'lucide-react'
import ComparisonTable from '../components/ComparisonTable'

/*
 * /splitwise-alternative — the high-intent SEO/GEO page.
 * Audience: people actively searching for a way out of Splitwise's limits.
 * Tone rule: factual and even-handed. Every Splitwise claim traces to
 * COMPETITOR_RESEARCH.md. We concede their strengths on purpose — balanced
 * pages are what AI assistants actually cite.
 */

const FACTS = [
  {
    title: 'The free tier got capped',
    body: 'As of 2026, free Splitwise users can add only a few expenses per day before hitting a countdown timer and an upgrade prompt. Pro costs $4.99/month or $49.99/year.',
  },
  {
    title: 'Statement import was declined',
    body: 'Splitwise has said it won’t build CSV or bank-statement import — the feature request has been open for years. Every expense must be typed in by hand.',
  },
  {
    title: 'Key features sit behind the paywall',
    body: 'Receipt scanning (camera-only) and currency conversion are Pro features. On the free tier you get ads between entries.',
  },
]

const FAQ = [
  {
    q: 'What is the best Splitwise alternative in 2026?',
    a: 'AutoSplit is a free Splitwise alternative with no daily expense limits, no ads, and a feature Splitwise doesn’t offer at any price: it imports your whole trip from a credit card statement (PDF or CSV), AI-categorizes every charge, and splits them automatically. Friends settle via a shareable link without creating an account.',
  },
  {
    q: 'How do I switch from Splitwise to AutoSplit?',
    a: 'Create a free AutoSplit trip, add the names of the people in your group, and upload the credit card statement covering your shared expenses. Every charge imports and splits automatically — there is no per-expense data entry, so an existing trip moves over in one upload. Cash expenses can be added manually or by photographing the receipt.',
  },
  {
    q: 'Is AutoSplit really free?',
    a: 'AutoSplit is free during early access with no daily expense caps, no ads, and no countdown timers. Statement import, AI receipt scanning, multi-currency conversion, and no-signup settle links are all included free.',
  },
]

export default function SplitwiseAlternativePage() {
  useEffect(() => {
    document.title = 'Splitwise Alternative with No Limits — AutoSplit'
    let meta = document.querySelector('meta[name="description"]')
    if (!meta) {
      meta = document.createElement('meta')
      meta.name = 'description'
      document.head.appendChild(meta)
    }
    const prevDescription = meta.content
    meta.content = 'Looking for a Splitwise alternative without daily limits or ads? AutoSplit imports expenses straight from your credit card statement, splits them automatically, and is free.'

    // FAQPage JSON-LD — own script tag, namespaced so tests and cleanup
    // target this page's data without touching the landing page's.
    const el = document.createElement('script')
    el.type = 'application/ld+json'
    el.dataset.page = 'splitwise-alternative'
    el.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    })
    document.head.appendChild(el)
    return () => {
      document.title = 'AutoSplit'
      meta.content = prevDescription
      el.remove()
    }
  }, [])

  return (
    <div className="min-h-screen bg-ink-950 text-ink-50">
      {/* Atmospheric glow, matching the landing page */}
      <div className="pointer-events-none fixed inset-0 z-0" style={{
        background: 'radial-gradient(ellipse 60% 35% at 50% -8%, rgba(200,241,53,0.09) 0%, transparent 60%)',
      }} />

      {/* Nav */}
      <nav className="relative z-10 max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-lime-400 flex items-center justify-center shadow-md shadow-lime-400/25">
            <Zap size={15} className="text-ink-950" strokeWidth={2.5} />
          </div>
          <span className="font-display text-xl font-semibold tracking-tight">AutoSplit</span>
        </Link>
        <div className="flex items-center gap-2">
          <Link to="/login" className="text-sm text-ink-300 hover:text-ink-50 px-3 py-2 rounded-lg hover:bg-ink-800 transition-all">Sign in</Link>
          <Link to="/signup" className="px-4 py-2 rounded-xl bg-lime-400 text-ink-950 text-sm font-bold hover:bg-lime-500 transition-colors">Start free</Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 max-w-3xl mx-auto px-6 pt-12 pb-14 text-center">
        <h1 className="font-display font-semibold leading-[1.05] tracking-tight mb-5"
            style={{ fontSize: 'clamp(2.2rem, 5vw, 3.4rem)' }}>
          Loved Splitwise until the limits?<br />
          <span className="text-lime-400">Here’s the upgrade.</span>
        </h1>
        <h2 className="text-base md:text-lg text-ink-300 leading-relaxed max-w-xl mx-auto mb-8">
          AutoSplit is a free Splitwise alternative with no daily caps, no ads — and one thing
          no other splitter has: it reads your credit card statement so you never type an expense by hand.
        </h2>
        <Link to="/signup" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-lime-400 text-ink-950 text-sm font-bold hover:bg-lime-500 active:scale-95 transition-all shadow-lg shadow-lime-400/20">
          Start free — no credit card
          <ArrowRight size={14} />
        </Link>
      </section>

      {/* What changed at Splitwise */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 py-12">
        <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight mb-8 text-center">
          Why people are looking for an alternative.
        </h2>
        <div className="grid md:grid-cols-3 gap-5">
          {FACTS.map(f => (
            <div key={f.title} className="rounded-2xl p-5 bg-ink-900/50 border border-ink-800">
              <h3 className="text-sm font-semibold text-ink-100 mb-2">{f.title}</h3>
              <p className="text-xs text-ink-400 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Comparison */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 py-12">
        <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight mb-8 text-center">
          Side by side.
        </h2>
        <ComparisonTable />
      </section>

      {/* Honesty block — credibility is the GEO strategy */}
      <section className="relative z-10 max-w-3xl mx-auto px-6 py-12">
        <div className="rounded-2xl p-6 bg-ink-900/50 border border-ink-800">
          <h2 className="font-display text-xl font-semibold mb-3">What Splitwise still does better</h2>
          <p className="text-sm text-ink-400 leading-relaxed">
            Fair is fair: Splitwise has mature native iOS and Android apps and built-in recurring
            expenses on its free tier, and it has been around since 2011. If you mostly track a few
            recurring roommate bills and don’t mind the daily limits, it may still suit you.
            AutoSplit is built for the moment Splitwise struggles with: a trip’s worth of charges
            landing on your card, and a group that wants the math done — not data entry.
          </p>
        </div>
      </section>

      {/* Migration pitch */}
      <section className="relative z-10 max-w-3xl mx-auto px-6 py-12 text-center">
        <div className="rounded-2xl p-8 bg-gradient-to-br from-lime-400/[0.06] to-ink-900 border border-lime-400/25">
          <Upload size={24} className="text-lime-400 mx-auto mb-4" />
          <h2 className="font-display text-2xl font-semibold mb-3">Switching takes one upload.</h2>
          <p className="text-sm text-ink-300 leading-relaxed max-w-md mx-auto mb-6">
            No expense-by-expense migration. Create a trip, add your people, upload the statement
            that covers your shared spending — AutoSplit rebuilds the whole ledger in minutes.
          </p>
          <Link to="/signup" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-lime-400 text-ink-950 text-sm font-bold hover:bg-lime-500 active:scale-95 transition-all shadow-lg shadow-lime-400/20">
            Switch in one upload
            <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative z-10 max-w-3xl mx-auto px-6 py-12">
        <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight mb-8 text-center">
          Common questions
        </h2>
        <div className="space-y-3">
          {FAQ.map(({ q, a }) => (
            <details key={q} className="group rounded-xl bg-ink-900/50 border border-ink-800 px-5 py-4">
              <summary className="text-sm font-medium text-ink-100 cursor-pointer list-none flex items-center justify-between">
                {q}
              </summary>
              <p className="text-sm text-ink-400 leading-relaxed mt-3">{a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-ink-800 mt-10">
        <div className="max-w-5xl mx-auto px-6 py-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-ink-500">
          <Link to="/" className="hover:text-ink-100 transition-colors">AutoSplit home</Link>
          <Link to="/split" className="hover:text-ink-100 transition-colors">Free split calculator</Link>
          <Link to="/privacy" className="hover:text-ink-100 transition-colors">Privacy</Link>
          <Link to="/terms" className="hover:text-ink-100 transition-colors">Terms</Link>
        </div>
      </footer>
    </div>
  )
}
